import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { IRestrictedArea } from '@d-orca/shared/alert-kinds';
import { professionOptionLabel } from '@d-orca/shared/client/utils';
import { InfoTip } from '@components/ui/InfoTip/InfoTip';
import styles from './RestrictedAreaTitle.module.css';

/** Who works for each profession, by profession id — the Hub has it from the
 *  restricted-area options; the personal page names professions by trade alone. */
export type ContractorsById = ReadonlyMap<string, string>;

export interface IRestrictedAreaTitleProps {
  area: IRestrictedArea;
  contractors?: ContractorsById;
  /** Set: an "Edit" link-button after the title (the Alerts Hub). */
  onEdit?: () => void;
}

const K = 'alertSettings.restrictedArea';

/** The place in one phrase: a single floor or area by name, else a count — floors win. */
function placeLabel(t: TFunction, area: IRestrictedArea): string {
  const places = [...area.floors, ...area.areas];
  if (places.length === 1) return places[0]!.name;
  return area.floors.length > 0
    ? t(`${K}.floors`, { count: area.floors.length })
    : t(`${K}.areas`, { count: area.areas.length });
}

function professionNames(t: TFunction, area: IRestrictedArea, contractors?: ContractorsById): string[] {
  if (area.professions === 'all') return [t(`${K}.allProfessions`)];
  return area.professions.map((p) => professionOptionLabel(t, { tradeId: p.tradeId, contractor: contractors?.get(p.id) }));
}

function professionsLabel(t: TFunction, area: IRestrictedArea, names: string[]): string {
  if (area.professions === 'all' || names.length === 1) return names[0]!;
  return t(`${K}.professions`, { count: names.length });
}

/** ", permitted with: …" — nothing when no certificate lets a worker in. */
function certificatesLabel(t: TFunction, area: IRestrictedArea): string {
  if (area.certificates === 'all') return t(`${K}.permittedWith`, { what: t(`${K}.allCertificates`) });
  if (area.certificates.length === 0) return '';
  return t(`${K}.permittedWith`, { what: t(`${K}.certificates`, { count: area.certificates.length }) });
}

/** Everything the title abbreviates, one line per part, for the ⓘ. */
function detailLines(t: TFunction, area: IRestrictedArea, professions: string[]): string[] {
  const names = (items: { name: string }[]) => items.map((i) => i.name).join(', ');
  const certificates = area.certificates === 'all' ? t(`${K}.allCertificates`) : names(area.certificates);
  return [
    area.floors.length > 0 ? t(`${K}.infoFloors`, { list: names(area.floors) }) : '',
    area.areas.length > 0 ? t(`${K}.infoAreas`, { list: names(area.areas) }) : '',
    t(`${K}.infoProfessions`, { list: professions.join(', ') }),
    certificates ? t(`${K}.infoCertificates`, { list: certificates }) : '',
  ].filter(Boolean);
}

/**
 * A restricted area's row title: its place in bold, who it is closed to and
 * which certificates let a worker in anyway, an ⓘ with the full lists, and —
 * on the Hub — an Edit link-button.
 */
export function RestrictedAreaTitle({ area, contractors, onEdit }: IRestrictedAreaTitleProps) {
  const { t } = useTranslation();
  const professions = professionNames(t, area, contractors);
  const restriction =
    t(`${K}.restrictedFor`, { who: professionsLabel(t, area, professions) }) + certificatesLabel(t, area);

  return (
    <div className={styles.title}>
      <div className={styles.placeLine}>
        <span className={styles.place}>{placeLabel(t, area)}</span>
        <InfoTip text={t('alertSettings.kinds.safety.restrictedArea.name')} lines={detailLines(t, area, professions)} />
        {onEdit && (
          <button type="button" className={styles.edit} onClick={onEdit}>
            {t(`${K}.edit`)}
          </button>
        )}
      </div>
      <span className={styles.restriction}>{restriction}</span>
    </div>
  );
}
